/**
 * Shell-level celebration store.
 *
 * The `CelebrationMoment` overlay (the restrained chip that names a
 * freshly-decided opportunity per design README section 5.7) is fired
 * from two surfaces: a drag onto the WON / LOST columns on the
 * `OpportunitiesBoard`, and the "Mark won" / "Mark lost" buttons in
 * the opportunity slide-over. Both used to manage local state inside
 * the board, which meant the slide-over buttons worked server-side
 * but silently closed with no visual confirmation (UAT 7.7 through
 * 7.9).
 *
 * Lifting the trigger into a Zustand slice mounted at the shell solves
 * the visibility gap without prop-drilling. `__root.tsx` renders the
 * overlay against this store; any view that lands a WON or LOST
 * outcome calls `useCelebration.getState().show(name, outcome)` and
 * the chip animates in. The LOST variant is intentionally a quieter
 * grey one-line confirmation rather than a celebration — same surface,
 * different framing, no false enthusiasm.
 *
 * Reference: spec section 15.8, design README section 5.7, RD doc on
 * gamification anti-patterns (single chip, no streaks, no sounds).
 */

import { create } from 'zustand';

/**
 * Which slide-over outcome raised the overlay. WON renders an orange
 * "celebration" chip; LOST renders a quieter neutral chip so the user
 * still sees the action took effect without the visual implying a win.
 */
export type CelebrationOutcome = 'WON' | 'LOST';

interface CelebrationState {
  /** Opportunity name to render in the chip, or null when idle. */
  opportunityName: string | null;
  /** Which framing to render. Null while idle. */
  outcome: CelebrationOutcome | null;
  /** Queue an overlay for the given opportunity name + outcome. */
  show: (opportunityName: string, outcome: CelebrationOutcome) => void;
  /** Clear the overlay. Auto-fired by the shell after the animation. */
  dismiss: () => void;
}

/**
 * Zustand slice for the celebration overlay. Held at module scope so
 * mutation observers and route changes share the same instance.
 */
export const useCelebration = create<CelebrationState>((set) => ({
  opportunityName: null,
  outcome: null,
  show: (opportunityName, outcome) => set({ opportunityName, outcome }),
  dismiss: () => set({ opportunityName: null, outcome: null }),
}));
